import PageBackTitle from "../Component/PageBackTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Select from "react-select";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import Pagination from "../Component/Pagination";
import { hrRecordsApi } from "../Services/hrRecordsApi";
import {
  formatDateTimeIST,
  formatFileSize,
  getApiErrorMessage,
} from "./hrRecordsHelpers";
import "../tables/User.css";
import "./hrRecords.css";
import "./EmailLogPage.css";

const emptyFilters = {
  source: "",
  status: "",
  search: "",
  from_date: "",
  to_date: "",
};

const defaultStatusOptions = [
  { value: "", label: "All Status" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
];

const toTitle = (value = "") =>
  value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "NA";

const getStatusClass = (status = "") => {
  switch (status.toLowerCase()) {
    case "sent":
    case "success":
      return "email-log-status-success";
    case "failed":
    case "error":
      return "email-log-status-failed";
    default:
      return "email-log-status-neutral";
  }
};

const buildOptionList = (values, allLabel) => [
  { value: "", label: allLabel },
  ...Array.from(new Set(values.filter(Boolean))).map((value) => ({
    value,
    label: toTitle(value),
  })),
];

const selectMenuStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 1300 }),
};

const isHtmlBody = (value = "") =>
  /<\/?[a-z][\s\S]*>/i.test(String(value || ""));

const buildEmailPreviewHtml = (body = "") => {
  const text = String(body || "");
  if (!text) return "";

  if (/<!doctype html|<html[\s>]/i.test(text)) {
    return text;
  }

  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body>${text}</body></html>`;
};

function EmailLogPage({ isSidebarExpanded }) {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [activeFilters, setActiveFilters] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [paginationKey, setPaginationKey] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const sourceOptions = useMemo(() => {
    const values = [
      ...records.map((record) => record.source),
      ...summary.map((item) => item.source),
    ];
    return buildOptionList(values, "All Sources");
  }, [records, summary]);

  const statusOptions = useMemo(() => {
    const values = [
      ...defaultStatusOptions.map((option) => option.value),
      ...records.map((record) => record.status),
      ...summary.map((item) => item.status),
    ];
    return buildOptionList(values, "All Status");
  }, [records, summary]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...activeFilters,
        page,
        per_page: itemsPerPage,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === "") {
          delete params[key];
        }
      });

      const response = await hrRecordsApi.listEmailLog(params);
      setRecords(response.records || []);
      setSummary(response.summary || []);
      setTotalItems(response.TotalItems || 0);
    } catch (error) {
      console.error("Failed to load email logs:", error);
      showSnackbar(getApiErrorMessage(error, "Failed to load email logs"), "error");
    } finally {
      setLoading(false);
    }
  }, [activeFilters, itemsPerPage, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setActiveFilters(filters);
      setPage(1);
      setPaginationKey((prev) => prev + 1);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setActiveFilters(emptyFilters);
    setPage(1);
    setPaginationKey((prev) => prev + 1);
  };

  const handlePaginationChange = (currentPage, perPage) => {
    setPage(currentPage);
    setItemsPerPage(perPage);
  };

  const handleViewLog = async (logId) => {
    try {
      setDetailLoading(true);
      const response = await hrRecordsApi.getEmailLogEntry(logId);
      setSelectedLog(response);
    } catch (error) {
      console.error("Failed to load email log detail:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to load email log detail"),
        "error",
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const selectMenuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content email-log-header-content">
              <PageBackTitle>Email Log</PageBackTitle>
              <div className="data-list-heading-content-right-side email-log-header-tools">
                <div className="email-log-search">
                  <IoSearchOutline className="email-log-search-icon" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(event) =>
                      handleFilterChange("search", event.target.value)
                    }
                    placeholder="Search subject or recipient"
                  />
                </div>

                <Select
                  className="email-log-filter-select email-log-source-select"
                  classNamePrefix="email-log-select"
                  menuPortalTarget={selectMenuPortalTarget}
                  menuPosition="fixed"
                  styles={selectMenuStyles}
                  options={sourceOptions}
                  value={
                    sourceOptions.find(
                      (option) => option.value === filters.source,
                    ) || sourceOptions[0]
                  }
                  onChange={(option) =>
                    handleFilterChange("source", option?.value || "")
                  }
                  placeholder="All Sources"
                />

                <Select
                  className="email-log-filter-select email-log-status-select"
                  classNamePrefix="email-log-select"
                  menuPortalTarget={selectMenuPortalTarget}
                  menuPosition="fixed"
                  styles={selectMenuStyles}
                  options={statusOptions}
                  value={
                    statusOptions.find(
                      (option) => option.value === filters.status,
                    ) || statusOptions[0]
                  }
                  onChange={(option) =>
                    handleFilterChange("status", option?.value || "")
                  }
                  placeholder="All Status"
                />

                <input
                  className="email-log-filter-input email-log-date-filter"
                  type="date"
                  value={filters.from_date}
                  onChange={(event) =>
                    handleFilterChange("from_date", event.target.value)
                  }
                />

                <input
                  className="email-log-filter-input email-log-date-filter"
                  type="date"
                  value={filters.to_date}
                  onChange={(event) =>
                    handleFilterChange("to_date", event.target.value)
                  }
                />

                <button
                  className="person-user-cancel-button email-log-filter-button"
                  type="button"
                  onClick={resetFilters}
                  disabled={loading}
                >
                  Reset
                </button>

                <button
                  className="refresh-button email-log-refresh-button"
                  type="button"
                  onClick={fetchLogs}
                  disabled={loading}
                >
                  <TbRefresh className="refresh-icon" />
                </button>
              </div>
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-list-body">
            <div className="data-list-scroll-body email-log-scroll">
              {/* <div className="email-log-summary">
                {summary.length ? (
                  summary.map((item) => (
                    <div
                      className="email-log-summary-item"
                      key={`${item.source || "unknown"}-${item.status || "unknown"}`}
                    >
                      <div>{toTitle(item.source)}</div>
                      <strong>{item.count || 0}</strong>
                    </div>
                  ))
                ) : (
                  <div className="email-log-summary-item">
                    <div>Total Emails</div>
                    <strong>{totalItems}</strong>
                    <span>Current filters</span>
                  </div>
                )}
              </div> */}

              <div className="data-list-table email-log-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : records.length ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th>SR. NO.</th>
                        <th>SOURCE</th>
                        <th>VARIANT</th>
                        <th>FROM</th>
                        <th>TO</th>
                        <th>SUBJECT</th>
                        <th>FILE</th>
                        <th>STATUS</th>
                        <th>SENT AT</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {records.map((record, index) => (
                        <tr key={record.id} className="hoverable-row">
                          <td>{(page - 1) * itemsPerPage + index + 1}</td>
                          <td>{toTitle(record.source)}</td>
                          <td>{record.variant || "NA"}</td>
                          <td>{record.from_email || "NA"}</td>
                          <td className="email-log-truncate">
                            {record.to_emails || "NA"}
                          </td>
                          <td className="email-log-truncate">
                            {record.subject || "NA"}
                          </td>
                          <td>{record.attachment_count || 0}</td>
                          <td>
                            <span
                              className={`email-log-status ${getStatusClass(
                                record.status,
                              )}`}
                            >
                              {toTitle(record.status)}
                            </span>
                          </td>
                          <td>{formatDateTimeIST(record.sent_at)}</td>
                          <td>
                            <button
                              className="email-log-view-button"
                              type="button"
                              onClick={() => handleViewLog(record.id)}
                              disabled={detailLoading}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">No Email Logs Found</div>
                  </div>
                )}
              </div>

              <Pagination
                key={paginationKey}
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={handlePaginationChange}
              />
            </div>
          </div>
        </div>
      </div>

      {selectedLog && (
        <div className="email-log-modal-backdrop">
          <div className="email-log-modal">
            <div className="email-log-modal-header">
              <div>Email Detail</div>
              <button type="button" onClick={() => setSelectedLog(null)}>
                x
              </button>
            </div>
            <div className="email-log-modal-body">
              <div className="email-log-detail-grid">
                <div>
                  <label>Source</label>
                  <span>{toTitle(selectedLog.source)}</span>
                </div>
                <div>
                  <label>Status</label>
                  <span>{toTitle(selectedLog.status)}</span>
                </div>
                <div>
                  <label>From</label>
                  <span>{selectedLog.from_email || "NA"}</span>
                </div>
                <div>
                  <label>To</label>
                  <span>{selectedLog.to_emails || "NA"}</span>
                </div>
                <div>
                  <label>CC</label>
                  <span>{selectedLog.cc_emails || "NA"}</span>
                </div>
                <div>
                  <label>Sent At</label>
                  <span>{formatDateTimeIST(selectedLog.sent_at)}</span>
                </div>
                <div>
                  <label>Related ID</label>
                  <span>{selectedLog.related_id || "NA"}</span>
                </div>
                <div>
                  <label>Variant</label>
                  <span>{selectedLog.variant || "NA"}</span>
                </div>
              </div>

              <div className="email-log-detail-block">
                <label>Subject</label>
                <div>{selectedLog.subject || "NA"}</div>
              </div>

              {selectedLog.error_message && (
                <div className="email-log-detail-block email-log-error-block">
                  <label>Error</label>
                  <div>{selectedLog.error_message}</div>
                </div>
              )}

              <div className="email-log-detail-block">
                <label>Body</label>
                {selectedLog.body ? (
                  isHtmlBody(selectedLog.body) ? (
                    <iframe
                      className="email-log-body-frame"
                      title="Email body preview"
                      sandbox=""
                      srcDoc={buildEmailPreviewHtml(selectedLog.body)}
                    />
                  ) : (
                    <pre>{selectedLog.body}</pre>
                  )
                ) : (
                  <div>NA</div>
                )}
              </div>

              <div className="email-log-detail-block">
                <label>Attachments</label>
                {selectedLog.attachments?.length ? (
                  <div className="email-log-attachments">
                    {selectedLog.attachments.map((attachment) => (
                      <div
                        className="email-log-attachment"
                        key={attachment.file_id || attachment.filename}
                      >
                        <strong>{attachment.filename || "Attachment"}</strong>
                        <span>
                          {[attachment.file_type, formatFileSize(attachment.file_size)]
                            .filter(Boolean)
                            .join(" | ")}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>NA</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default EmailLogPage;
